import {
  errorResponseSchema,
  successResponseSchema,
  type ErrorResponse,
  type Paginated,
} from '@nest-react-monorepo/contracts';
import { z } from 'zod';
import { ApiRequestError, ApiTimeoutError, UNKNOWN_REQUEST_ID } from './api-error.js';
import { currentLocale } from './i18n.js';
import { captureError } from './monitoring.js';

export { ApiRequestError, ApiTimeoutError, UNKNOWN_REQUEST_ID } from './api-error.js';

const BASE_URL = import.meta.env['VITE_API_URL'] ?? '/api/v1';

/**
 * Rule E8: `fetch` has no deadline of its own. A hung mutation must never
 * trap the user with no way out — the worst case in this application is a
 * posted currency exchange nobody can back out of. This bounds every
 * request so it always eventually settles, one way or another.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

interface RequestOptions<TResponse> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Response `data` schema. Parsing here means a backend change fails loudly. */
  schema?: z.ZodType<TResponse>;
  signal?: AbortSignal;
  /** Rule E8. Overridable for tests and long-running exceptions. */
  timeoutMs?: number;
  /** Extra request headers, e.g. `Idempotency-Key`. */
  headers?: Record<string, string>;
}

async function parseError(response: Response): Promise<ErrorResponse> {
  try {
    const json: unknown = await response.json();
    const parsed = errorResponseSchema.safeParse(json);
    if (parsed.success) return parsed.data;
  } catch {
    // Falls through to the synthetic envelope below.
  }

  return {
    success: false,
    statusCode: response.status,
    error: {
      code: response.status >= 500 ? 'INTERNAL_ERROR' : 'UNPROCESSABLE',
      message: response.statusText || 'Request failed',
    },
    requestId: response.headers.get('x-request-id') ?? UNKNOWN_REQUEST_ID,
    timestamp: new Date().toISOString(),
    locale: currentLocale(),
    path: response.url,
  };
}

export type { Paginated };

/**
 * The one place that calls `fetch` for this application. Session state lives
 * entirely in the `cur_session` cookie Unified Access's launch redemption
 * sets (`credentials: 'include'`) — there is no bearer token, no local
 * refresh flow, and no local login. A bounded deadline keeps the UI from
 * getting stuck on a hung request indefinitely.
 */
export async function apiRequest<TResponse = unknown>(
  path: string,
  options: RequestOptions<TResponse> = {},
): Promise<TResponse> {
  const {
    method = 'GET',
    body,
    schema,
    signal,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
    headers,
  } = options;

  // A caller that supplies its own `signal` (e.g. a TanStack Query `GET`)
  // keeps full, unwrapped ownership of it; that exact object still reaches
  // `fetch` unchanged, so a caller-cancelled request stays distinguishable
  // from this deadline firing.
  let timedOut = false;
  const ownDeadline = signal ? undefined : new AbortController();
  const requestSignal = signal ?? ownDeadline!.signal;
  const timer =
    timeoutMs > 0 && ownDeadline
      ? setTimeout(() => {
          timedOut = true;
          ownDeadline.abort();
        }, timeoutMs)
      : undefined;

  try {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        method,
        credentials: 'include',
        headers: {
          'Accept-Language': currentLocale(),
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...headers,
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        signal: requestSignal,
      });
    } catch (error) {
      throw timedOut ? new ApiTimeoutError() : error;
    }

    if (!response.ok) {
      const payload = await parseError(response);
      const failure = new ApiRequestError(response.status, payload);
      // captureError itself ignores 4xx; a rejected form is not an incident.
      captureError(failure, { path });
      throw failure;
    }

    if (response.status === 204) return undefined as TResponse;

    const json: unknown = await response.json();
    const envelope = successResponseSchema(schema ?? z.unknown()).parse(json);
    // The success envelope carries `meta` as a SIBLING of `data`
    // (packages/contracts/src/common/envelope.ts), not nested inside it. A
    // list endpoint's caller expects `{ data, meta }` back; a single-record
    // endpoint has `meta: null` and returns `data` bare. Dropping `meta`
    // here would silently starve every paginated list of its own response
    // shape — callers would still "work" but every list would render empty
    // regardless of how much data the server actually returned.
    if (envelope.meta === null || envelope.meta === undefined) return envelope.data as TResponse;
    return { data: envelope.data, meta: envelope.meta } as TResponse;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * An authenticated binary read, e.g. the exchange photo in الصناديق → عرض
 * (currency.md section 3.2). Fetched with the session cookie and shown from
 * an object URL, so the API never has to serve images cross-origin.
 */
export async function apiBlob(path: string): Promise<Blob> {
  const response = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Accept-Language': currentLocale() },
  });
  if (!response.ok) throw new ApiRequestError(response.status, await parseError(response));
  return response.blob();
}

/** Export files stay authenticated at download time via the session cookie. */
export async function apiDownload(path: string, body: unknown): Promise<void> {
  const send = (): Promise<Response> =>
    fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Accept-Language': currentLocale(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  const response = await send();
  if (!response.ok) throw new ApiRequestError(response.status, await parseError(response));
  const blob = await response.blob();
  const fileName =
    response.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ??
    'currency-export.xlsx';
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName.replace(/[/\\]/g, '_');
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
